import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";

/** Модал «Экономика заявки» (запрос клиента: динамическая таблица в
 *  мини-аппе). Открывается из карточки заявки в реестре. Доступ —
 *  require_audit (бэкенд). */

export interface DealEconomicsData {
  input: {
    deal_id: number;
    deal_type: string;
    deal_date: string | null;
    currency: string;
    amount: number;
    base_currency: string;
    fix_rate: number | null;
    body_rub: number | null;
    company_id: number;
    subcontractor_id: number | null;
    counterparty_id: number | null;
    fee_percent: number;
    fixed_commission_amount: number | null;
    fixed_commission_currency: string | null;
    deal_format: string;
    status: string;
  };
  expenses: Array<{
    kind: string;
    title: string;
    qty?: number;
    rate_rub?: number;
    amount_rub: number;
    scheme?: string;
    rate?: number | null;
    currency?: string;
    accrued?: boolean;
    paid?: boolean;
  }>;
  income: {
    commission_rub: number;
    fixed_commission_rub: number;
  };
  totals: {
    rub: number;
    percent_of_body: number | null;
    fx_impact_rub: number;
    commission_total_rub: number;
    agents_payout_rub: number;
    lots_cost_rub: number;
  };
}

function fmt(n: number | null | undefined, digits: number = 2): string {
  if (n == null) return "—";
  return n.toLocaleString("ru-RU", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
}

export function DealEconomicsModal({
  dealId, onClose,
}: {
  dealId: number;
  onClose: () => void;
}) {
  const [data, setData] = useState<DealEconomicsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get<DealEconomicsData>(`/api/deals/${dealId}/economics`)
      .then(setData)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [dealId]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-30"
      onClick={onClose}
    >
      <div
        className="bg-tg-bg rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl p-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">💼 Экономика заявки №{dealId}</h2>
          <button onClick={onClose} className="text-tg-hint text-xl px-2">×</button>
        </div>

        {loading && (
          <div className="text-tg-hint text-sm animate-pulse">Загрузка…</div>
        )}
        {error && (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
            {error}
          </div>
        )}

        {data && (
          <>
            {/* === INPUT === */}
            <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
              <div className="text-xs font-medium text-tg-hint uppercase mb-2">
                Входящие параметры
              </div>
              <Row label="Тип" value={data.input.deal_type === "import" ? "Импорт" : "Экспорт"} />
              <Row label="Формат" value={data.input.deal_format} />
              <Row label="Сумма заявки"
                   value={`${fmt(data.input.amount)} ${data.input.currency}`} />
              <Row label="Курс фикса" value={fmt(data.input.fix_rate, 4)} />
              <Row label="Тело платежа в ₽" value={`${fmt(data.input.body_rub)} ₽`} bold />
              <Row label="Комиссия %" value={`${fmt(data.input.fee_percent, 4)}%`} />
              {data.input.fixed_commission_amount != null && (
                <Row label="Фикс. комиссия"
                     value={`${fmt(data.input.fixed_commission_amount)} ${data.input.fixed_commission_currency || ""}`} />
              )}
              <Row label="Дата заявки" value={data.input.deal_date || "—"} />
              <Row label="Статус" value={data.input.status} />
            </section>

            {/* === EXPENSES === */}
            <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
              <div className="text-xs font-medium text-tg-hint uppercase mb-2">
                Расходная часть
              </div>
              {data.expenses.length === 0 ? (
                <div className="text-sm text-tg-hint">Нет расходных статей.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-tg-hint">
                    <tr>
                      <th className="text-left py-1">Статья</th>
                      <th className="text-right py-1">Сумма, ₽</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenses.map((e, i) => (
                      <tr key={i} className="border-t border-tg-secondaryBg">
                        <td className="py-1.5">
                          {e.title}
                          {e.kind === "lot_cost" && e.qty != null && e.rate_rub != null && (
                            <div className="text-xs text-tg-hint">
                              {fmt(e.qty)} × {fmt(e.rate_rub, 4)} ₽
                            </div>
                          )}
                          {e.kind === "agent" && (
                            <div className="text-xs text-tg-hint">
                              {e.scheme}{e.rate != null ? ` · ${e.rate}%` : ""}
                              {e.paid && <span className="ml-1 text-emerald-600">· ✅ выплачено</span>}
                            </div>
                          )}
                        </td>
                        <td className="text-right py-1.5 tabular-nums">
                          {fmt(e.amount_rub)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* === INCOME === */}
            <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
              <div className="text-xs font-medium text-tg-hint uppercase mb-2">
                Доходная часть
              </div>
              <Row label="Комиссия (% от тела)"
                   value={`${fmt(data.income.commission_rub)} ₽`} />
              {data.income.fixed_commission_rub > 0 && (
                <Row label="Фикс. комиссия в ₽"
                     value={`${fmt(data.income.fixed_commission_rub)} ₽`} />
              )}
              <Row label="Итого доход"
                   value={`${fmt(data.totals.commission_total_rub)} ₽`} bold />
            </section>

            {/* === TOTALS === */}
            <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
              <div className="text-xs font-medium text-tg-hint uppercase mb-2">
                Итог по заявке
              </div>
              <Row label="Курсовые разницы (FX)"
                   value={
                     <span className={data.totals.fx_impact_rub >= 0 ? "text-emerald-600" : "text-red-600"}>
                       {data.totals.fx_impact_rub >= 0 ? "+" : ""}{fmt(data.totals.fx_impact_rub)} ₽
                     </span>
                   } />
              <Row label="Выплаты агентам"
                   value={`−${fmt(data.totals.agents_payout_rub)} ₽`} />
              <Row label="Себестоимость лотов"
                   value={`${fmt(data.totals.lots_cost_rub)} ₽`} />
              <hr className="my-2 border-tg-secondaryBg" />
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">Итого, ₽</span>
                <span className={
                  "text-xl font-bold tabular-nums " +
                  (data.totals.rub >= 0 ? "text-emerald-600" : "text-red-600")
                }>
                  {data.totals.rub >= 0 ? "+" : ""}{fmt(data.totals.rub)} ₽
                </span>
              </div>
              {data.totals.percent_of_body != null && (
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-sm text-tg-hint">% к телу платежа</span>
                  <span className={
                    "text-sm font-medium tabular-nums " +
                    (data.totals.percent_of_body >= 0 ? "text-emerald-600" : "text-red-600")
                  }>
                    {data.totals.percent_of_body >= 0 ? "+" : ""}
                    {fmt(data.totals.percent_of_body, 4)}%
                  </span>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Row({
  label, value, bold = false,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <div className="flex justify-between items-baseline py-1 text-sm">
      <span className="text-tg-hint">{label}</span>
      <span className={"tabular-nums " + (bold ? "font-semibold" : "")}>
        {value}
      </span>
    </div>
  );
}
